import * as Yup from 'yup'
import { TAttribute } from 'types'
import { TFlourCategory } from 'types/models/Flours'

export const LoginValidationSchema = Yup.object({
  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),
  password: Yup.string().required('Password is required'),
})

export const ResetPasswordValidationSchema = Yup.object({
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    .matches(
      /[^a-zA-Z\d]/,
      'Password must contain at least one special character'
    ),

  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('password'), null], 'Passwords must match'),
})

export const CreateUpdateUserValidationSchema = Yup.object({
  title: Yup.string().required('Title is required'),
  name: Yup.string().required('Name is required'),
  surname: Yup.string().required('Last name is required'),
  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),
  password: Yup.string().required('Password is required'),
  company_roles: Yup.array()
    .min(1, 'At least one role is required')
    .of(
      Yup.object().shape({
        company_id: Yup.mixed()
          .transform((value) => (value === '' ? undefined : value))
          .required('Company is required'),
        role_id: Yup.mixed()
          .transform((value) => (value === '' ? undefined : value))
          .required('Role is required'),
      })
    ),
})

export const CreateEditCompany = Yup.object({
  name: Yup.string().required('Customer name is required'),
  parent_id: Yup.string().required('Parent company is required').nullable(),
  icon: new Yup.MixedSchema().required('Logo is required'),
})

const validateString = Yup.string()
  .required('Name is required')
  .min(3, 'Name must be at least 3 characters')

export const validateSiloSchema = Yup.object().shape({
  name: validateString,
  company_id: Yup.mixed<number | string>().optional(),
})

export const validateAttributes = async (
  attributes: TAttribute[],
  rules: Record<string, TRule>
) => {
  const newAttributesValidationErrors: Record<
    string,
    Record<string, string>
  > = {}

  // Generate the schema dynamically based on rules
  const attributeSchema = generateAttributesValidationSchema(rules)

  for (const attribute of attributes) {
    try {
      await attributeSchema.validate(attribute, { abortEarly: false })
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        if (!newAttributesValidationErrors[attribute.name]) {
          newAttributesValidationErrors[attribute.name] = {}
        }

        err.inner.forEach((error) => {
          const fieldName = error.path
          if (fieldName) {
            newAttributesValidationErrors[attribute.name][fieldName] =
              error.message
          }
        })
      }
    }
  }

  return newAttributesValidationErrors
}
export const createValidationSchema = (rules: Record<string, TRule>) => {
  return Yup.object().shape({
    id: Yup.number().optional(),
    name: Yup.string().required('Name is required'),
    company_id: Yup.mixed<number | string>().optional(),
    attributes: Yup.array()
      .of(generateAttributesValidationSchema(rules))
      .defined(),
  })
}

export const generateAttributesValidationSchema = (rules) => {
  return Yup.object().shape({
    name: Yup.string().required('Attribute name is required').defined(),

    range_min: Yup.number()
      .nullable()
      .notRequired()
      .transform((value) => {
        return value === '' || value === 0 ? null : value
      })
      .when('name', ([name], schema) => {
        const rule = rules[name]
        return rule
          ? schema.when([], (val, subSchema) =>
              val !== null && val !== undefined
                ? subSchema
                    .typeError('Must be a number')
                    .min(
                      rule.hardMin ?? rule.min,
                      `Must be at least ${rule.hardMin ?? rule.min}`
                    )
                    .max(
                      rule.hardMax ?? rule.max,
                      `Must be at most ${rule.hardMax ?? rule.max}`
                    )
                : subSchema
            )
          : schema
      }),

    range_max: Yup.number()
      .nullable()
      .notRequired()
      .transform((value) => {
        return value === '' ? null : value
      })
      .when('name', ([name], schema) => {
        const rule = rules[name]
        return rule
          ? schema.when([], (val, subSchema) =>
              val !== null && val !== undefined
                ? subSchema
                    .typeError('Must be a number')
                    .test(
                      'min-range',
                      'Must be greater than or equal to min',
                      function (value) {
                        const range_min = this.resolve(Yup.ref('range_min')) as
                          | number
                          | undefined
                        return (
                          value === undefined ||
                          range_min === undefined ||
                          value >= range_min
                        )
                      }
                    )
                    .max(
                      rule.hardMax ?? rule.max,
                      `Must be at most ${rule.hardMax ?? rule.max}`
                    )
                : subSchema
            )
          : schema
      }),

    // Rest of the schema remains the same
    range_avg: Yup.number()
      .nullable()
      .notRequired()
      .transform((value) => {
        return value === '' || value === 0 ? null : value
      })
      .when('name', ([name], schema) => {
        const rule = rules[name]
        return rule
          ? schema.when([], (val, subSchema) =>
              val !== null && val !== undefined
                ? subSchema
                    .typeError('Must be a number')
                    .min(
                      rule.hardMin ?? rule.min,
                      `Must be at least ${rule.hardMin ?? rule.min}`
                    )
                    .max(
                      rule.hardMax ?? rule.max,
                      `Must be at most ${rule.hardMax ?? rule.max}`
                    )
                : subSchema
            )
          : schema
      }),

    min_value: Yup.number().nullable().notRequired(),
    max_value: Yup.number().nullable().notRequired(),
    warn_above: Yup.number().nullable().notRequired(),
    hard_max: Yup.number().nullable().notRequired(),
    warn_below: Yup.number().nullable().notRequired(),
    hard_min: Yup.number().nullable().notRequired(),
  })
}

export const getAttributeWarnings = (
  attributes: TAttribute[],
  rules: Record<string, TRule>
): Record<
  string,
  { range_min?: string; range_max?: string; range_avg?: string }
> => {
  const warningMap: Record<
    string,
    { range_min?: string; range_max?: string; range_avg?: string }
  > = {}

  if (!Array.isArray(attributes)) {
    return warningMap
  }

  attributes.forEach((attr) => {
    const rule = rules[attr.name]
    if (!rule) return

    const min = Number(attr.range_min)
    const max = Number(attr.range_max)
    const avg = Number(attr.range_avg)

    if (!warningMap[attr.name]) warningMap[attr.name] = {}

    // range_max warning
    if (
      rule.warnAbove !== undefined &&
      rule.hardMax !== undefined &&
      !isNaN(max) &&
      max > rule.warnAbove &&
      max <= rule.hardMax
    ) {
      warningMap[attr.name].range_max = `Exceeds recommended range`
    }
    // range_min warning
    if (
      rule.warnBelow !== undefined &&
      rule.hardMin !== undefined &&
      !isNaN(min) &&
      min < rule.warnBelow &&
      min >= rule.hardMin
    ) {
      warningMap[attr.name].range_min = `Exceeds recommended range`
    }

    // range_avg warning
    if (
      (rule.warnAbove !== undefined &&
        rule.hardMax !== undefined &&
        !isNaN(avg) &&
        avg > rule.warnAbove &&
        avg <= rule.hardMax) ||
      (rule.warnBelow !== undefined &&
        rule.hardMin !== undefined &&
        !isNaN(avg) &&
        avg < rule.warnBelow &&
        avg >= rule.hardMin)
    ) {
      warningMap[attr.name].range_avg = `Exceeds recommended range`
    }
  })

  return warningMap
}

export type TRule = {
  min: number
  max: number
  warnAbove?: number
  hardMax?: number
  warnBelow?: number
  hardMin?: number
}

export const createNestedValidationSchema = (
  categories: TFlourCategory[],
  rules: Record<string, TRule>
) => {
  if (categories && rules) {
    return Yup.object().shape({
      id: Yup.number().optional(),
      name: Yup.string().required('Name is required'),
      company_id: Yup.number().required('Company ID is required'),
      attributes: Yup.object().shape(
        Object.fromEntries(
          categories.map((category) => [
            category.id,
            Yup.object().shape(
              Object.fromEntries(
                category.attributes.map((attr) => [
                  attr.id,
                  Yup.object().shape({
                    min: Yup.number()
                      .transform((_, originalValue) =>
                        originalValue === '' || isNaN(Number(originalValue))
                          ? undefined
                          : Number(originalValue)
                      )
                      .required('Min is required')
                      .test(
                        'bypass-validation-for-zero',
                        'Invalid value',
                        function (value) {
                          // If attribute is mandatory, don't bypass validation for zero
                          if (attr.is_mandatory && value === 0) {
                            const hardMin =
                              rules[attr.name]?.hardMin ??
                              rules[attr.name]?.min ??
                              -Infinity
                            if (value < hardMin) {
                              return this.createError({
                                message: `Must be at least ${hardMin}`,
                              })
                            }
                          }

                          // If value is 0 and attribute is not mandatory, bypass all validation
                          if (value === 0 && !attr.is_mandatory) {
                            return true
                          }

                          // For non-zero values, apply normal validation
                          const hardMin =
                            rules[attr.name]?.hardMin ??
                            rules[attr.name]?.min ??
                            -Infinity
                          const hardMax =
                            rules[attr.name]?.hardMax ??
                            rules[attr.name]?.max ??
                            Infinity

                          if (value < hardMin) {
                            return this.createError({
                              message: `Must be at least ${hardMin}`,
                            })
                          }

                          if (value > hardMax) {
                            return this.createError({
                              message: `Must be at most ${hardMax}`,
                            })
                          }

                          return true
                        }
                      ),

                    max: Yup.number()
                      .transform((_, originalValue) =>
                        originalValue === '' || isNaN(Number(originalValue))
                          ? undefined
                          : Number(originalValue)
                      )
                      .required('Max is required')
                      .test(
                        'bypass-validation-for-zero',
                        'Invalid value',
                        function (value) {
                          const min = this.resolve(Yup.ref('min'))

                          // If attribute is mandatory, don't bypass validation for zero
                          if (attr.is_mandatory && value === 0) {
                            const hardMin =
                              rules[attr.name]?.hardMin ??
                              rules[attr.name]?.min ??
                              -Infinity
                            if (value < hardMin) {
                              return this.createError({
                                message: `Must be at least ${hardMin}`,
                              })
                            }
                          }

                          // If value is 0 AND min is also 0 AND attribute is not mandatory, bypass all validation
                          if (value === 0 && min === 0 && !attr.is_mandatory) {
                            return true
                          }

                          // For non-zero values OR when min is not 0 OR attribute is mandatory, apply normal validation
                          const hardMin =
                            rules[attr.name]?.hardMin ??
                            rules[attr.name]?.min ??
                            -Infinity
                          const hardMax =
                            rules[attr.name]?.hardMax ??
                            rules[attr.name]?.max ??
                            Infinity

                          if (value < hardMin) {
                            return this.createError({
                              message: `Must be at least ${hardMin}`,
                            })
                          }

                          if (value > hardMax) {
                            return this.createError({
                              message: `Must be at most ${hardMax}`,
                            })
                          }

                          return true
                        }
                      )
                      .test(
                        'min-less-than-max',
                        'Must be greater than or equal to min',
                        function (value) {
                          const min = this.resolve(Yup.ref('min'))

                          // If both values are 0 AND attribute is not mandatory, bypass this validation
                          if (value === 0 && min === 0 && !attr.is_mandatory) {
                            return true
                          }

                          return (
                            value === undefined ||
                            min === undefined ||
                            value >= Number(min)
                          )
                        }
                      ),
                  }),
                ])
              )
            ),
          ])
        )
      ),
    })
  }
}

export const getNestedAttributeWarnings = (
  nestedAttributes: Record<string, Record<string, any>>,
  categoriesWithAttributes: TFlourCategory[],
  rules: Record<string, TRule>
) => {
  const warningMap = {}

  if (!nestedAttributes || !categoriesWithAttributes || !rules) {
    return warningMap
  }

  Object.entries(nestedAttributes).forEach(([categoryId, attributes]) => {
    const category = categoriesWithAttributes.find(
      (cat) => cat.id === Number(categoryId)
    )

    if (!category) return

    Object.entries(attributes).forEach(([attrId, values]) => {
      const attribute = category.attributes.find(
        (attr) => attr.id === Number(attrId)
      )

      if (!attribute || !attribute.name) return

      const rule = rules[attribute.name]
      if (!rule) return

      const min = Number(values.min)
      const max = Number(values.max)

      if (!warningMap[attribute.name]) warningMap[attribute.name] = {}

      // range_max warning
      if (
        rule.warnAbove !== undefined &&
        rule.hardMax !== undefined &&
        !isNaN(max) &&
        max > rule.warnAbove &&
        max <= rule.hardMax
      ) {
        warningMap[attribute.name].range_max = `Exceeds recommended range`
      }

      // range_min warning
      if (
        rule.warnBelow !== undefined &&
        rule.hardMin !== undefined &&
        !isNaN(min) &&
        min < rule.warnBelow &&
        min >= rule.hardMin
      ) {
        warningMap[attribute.name].range_min = `Exceeds recommended range`
      }
    })
  })

  return warningMap
}
